import React from 'react';
import { 
  LayoutDashboard, 
  Database, 
  ClipboardList, 
  DollarSign, 
  Menu,
  CheckSquare,
  FileText,
  MessageSquare,
  Lightbulb,
  FolderKanban,
  Users,
  BarChart3,
  Image,
  FolderOpen,
  Upload,
  FileSpreadsheet,
  Activity
} from 'lucide-react';

const MobileBottomNav = ({ activeTab, setActiveTab, user, hasAccess, setShowMoreMenu, showMoreMenu }) => {
  // Define main navigation items (show 4 + More)
  const mainNavItems = [
    { id: 'merged', icon: Database, label: 'Stock', permission: 'stock' },
    { id: 'orders', icon: ClipboardList, label: 'Orders', permission: 'orders' },
    { id: 'pricelist', icon: DollarSign, label: 'Prices', permission: 'pricelist' },
    { id: 'accounts', icon: FileText, label: 'Accounts', permission: 'accounts' },
  ];

  // More menu items - grouped logically
  const moreMenuItems = [
    // WINLIFE-specific first
    { id: 'upload', icon: Upload, label: 'Upload', winlifeOnly: true },
    { id: 'view', icon: FileSpreadsheet, label: 'View Sheet', winlifeOnly: true },
    // Core
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard' },
    { id: 'todo', icon: CheckSquare, label: 'To-Do', permission: 'todo' },
    { id: 'planning', icon: Lightbulb, label: 'Planning', permission: 'product_planning' },
    { id: 'projects', icon: FolderKanban, label: 'Projects', permission: 'projects' },
    { id: 'analytics', icon: BarChart3, label: 'Analytics', permission: 'analytics' },
    { id: 'activity', icon: Activity, label: 'Activity', operatorOnly: true },
    // Admin-only
    { id: 'messaging', icon: MessageSquare, label: 'Messages', permission: 'messaging' },
    { id: 'media', icon: Image, label: 'Media', permission: 'media' },
    { id: 'documents', icon: FolderOpen, label: 'Documents', permission: 'documents' },
    { id: 'users', icon: Users, label: 'Users', adminOnly: true },
  ];

  const filteredMainNav = mainNavItems.filter(item => hasAccess(item.permission));
  const filteredMoreNav = moreMenuItems.filter(item => {
    if (item.winlifeOnly) return user.role === 'winlife';
    if (item.adminOnly) return user.role === 'admin';
    if (item.operatorOnly) return user.role === 'admin' || user.role === 'winlife';
    return hasAccess(item.permission);
  });

  const isActiveInMore = filteredMoreNav.some(item => item.id === activeTab);

  return (
    <>
      {/* More Menu Overlay */}
      {showMoreMenu && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setShowMoreMenu(false)}
        />
      )}

      {/* More Menu Panel */}
      {showMoreMenu && (
        <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-slate-200 shadow-2xl z-50 lg:hidden animate-slide-up rounded-t-2xl max-h-[70vh] overflow-y-auto">
          <div className="p-4">
            <div className="flex justify-between items-center mb-3 sticky top-0 bg-white pb-2">
              <h3 className="text-sm font-semibold text-slate-700">More Options</h3>
              <button 
                onClick={() => setShowMoreMenu(false)}
                className="p-1 rounded-full hover:bg-slate-100"
                data-testid="mobile-more-close"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {filteredMoreNav.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setShowMoreMenu(false);
                  }}
                  data-testid={`mobile-more-${item.id}`}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl transition-all ${
                    activeTab === item.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-slate-50 text-slate-600 hover:bg-slate-100 active:bg-slate-200'
                  }`}
                >
                  <item.icon className="w-5 h-5 mb-1" />
                  <span className="text-[10px] font-medium text-center leading-tight">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-40 lg:hidden safe-area-bottom">
        <div className="flex justify-around items-center h-16 px-2">
          {filteredMainNav.slice(0, 4).map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                setShowMoreMenu(false);
              }}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-all ${
                activeTab === item.id
                  ? 'text-blue-600'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <div className={`p-1.5 rounded-lg transition-all ${
                activeTab === item.id ? 'bg-blue-50' : ''
              }`}>
                <item.icon className={`w-5 h-5 ${activeTab === item.id ? 'stroke-[2.5]' : ''}`} />
              </div>
              <span className={`text-[10px] mt-0.5 font-medium ${
                activeTab === item.id ? 'text-blue-600' : ''
              }`}>
                {item.label}
              </span>
            </button>
          ))}
          
          {/* More Button */}
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-all ${
              showMoreMenu || isActiveInMore
                ? 'text-blue-600'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <div className={`p-1.5 rounded-lg transition-all ${
              showMoreMenu || isActiveInMore ? 'bg-blue-50' : ''
            }`}>
              <Menu className={`w-5 h-5 ${showMoreMenu || isActiveInMore ? 'stroke-[2.5]' : ''}`} />
            </div>
            <span className={`text-[10px] mt-0.5 font-medium ${
              showMoreMenu || isActiveInMore ? 'text-blue-600' : ''
            }`}>
              More
            </span>
          </button>
        </div>
      </nav>

      {/* Spacer to prevent content from being hidden behind bottom nav */}
      <div className="h-16 lg:hidden" />

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.2s ease-out;
        }
        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0);
        }
      `}</style>
    </>
  );
};

export default MobileBottomNav;
